import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Eye, Wand2, Code2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';
import BlocksEditor from '../../../components/admin/blocks-editor/BlocksEditor';

type StepStatus = 'draft' | 'published';

interface StepForm {
  title: string;
  step_id: string;
  description: string;
  icon: string;
  status: StepStatus;
  blocksRaw: string;
}

interface StepMeta {
  order_index: number;
  parcours_id: string;
}

interface ParcoursParent {
  id: string;
  company_name: string;
  slug: string;
  status: string;
}

const EMPTY_FORM: StepForm = {
  title: '',
  step_id: '',
  description: '',
  icon: '',
  status: 'draft',
  blocksRaw: '[]',
};

function generateStepId(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Types de blocs réellement supportés par BlocksRenderer (avec leurs alias)
const KNOWN_BLOCK_TYPES = new Set([
  'rich-text',
  'image',
  'video',
  'embed-iframe',
  'pdf',
  'stats-grid', 'stats', 'stats_grid',
  'table',
  'timeline',
  'team-org', 'team_org',
]);

function blocksCount(raw: string): number {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

/** Liste des types inconnus rencontrés dans le JSON (pour alerter l'éditeur). */
function unknownBlockTypes(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const unknowns = new Set<string>();
    for (const b of parsed) {
      const t = b?.type;
      if (typeof t === 'string' && !KNOWN_BLOCK_TYPES.has(t)) unknowns.add(t);
    }
    return Array.from(unknowns);
  } catch {
    return [];
  }
}

export default function EditParcoursStepAdmin() {
  const { id, stepId } = useParams<{ id: string; stepId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState<StepForm>(EMPTY_FORM);
  const [meta, setMeta] = useState<StepMeta | null>(null);
  const [parcours, setParcours] = useState<ParcoursParent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stepIdManuallyEdited, setStepIdManuallyEdited] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<'visual' | 'json'>('visual');

  useEffect(() => {
    async function init() {
      if (!user) { navigate('/'); return; }
      const { data: ud } = await supabase.from('users').select('role').eq('id', user.id).maybeSingle();
      if (ud?.role !== 'admin' && ud?.role !== 'editor') { navigate('/'); return; }

      if (!id || !stepId) { setNotFound(true); setLoading(false); return; }

      try {
        // Charger le parcours parent
        const { data: p, error: pe } = await supabase
          .from('parcours')
          .select('id, company_name, slug, status')
          .eq('id', id)
          .maybeSingle();

        if (pe) throw pe;
        if (!p) { setNotFound(true); setLoading(false); return; }
        setParcours(p);

        // Charger l'étape
        const { data: step, error: se } = await supabase
          .from('parcours_steps')
          .select('*')
          .eq('id', stepId)
          .maybeSingle();

        if (se) throw se;
        if (!step || step.parcours_id !== id) { setNotFound(true); setLoading(false); return; }

        const blocks = Array.isArray(step.blocks) ? step.blocks : [];
        setForm({
          title: step.title ?? '',
          step_id: step.step_id ?? '',
          description: step.description ?? '',
          icon: step.icon ?? '',
          status: step.status ?? 'draft',
          blocksRaw: JSON.stringify(blocks, null, 2),
        });
        setMeta({ order_index: step.order_index, parcours_id: step.parcours_id });
        setStepIdManuallyEdited(true);
      } catch {
        toast.error('Impossible de charger l\'étape.');
        navigate(`/admin/parcours/${id}/steps`);
      } finally {
        setLoading(false);
      }
    }
    init();
    // user?.id (et pas user) pour ne pas re-fetch quand Supabase recrée
    // l'objet user à chaque token refresh / changement de visibilité d'onglet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, id, stepId]);

  function set<K extends keyof StepForm>(field: K, value: StepForm[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleTitleChange(value: string) {
    set('title', value);
    if (!stepIdManuallyEdited) {
      set('step_id', generateStepId(value));
    }
  }

  function handleBlocksChange(value: string) {
    set('blocksRaw', value);
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) {
        setJsonError('La valeur doit être un tableau JSON ( [...] ).');
      } else {
        setJsonError(null);
      }
    } catch (e) {
      setJsonError((e as Error).message);
    }
  }

  async function handleSave(targetStatus?: StepStatus) {
    const finalStatus = targetStatus ?? form.status;

    if (!form.title.trim()) { toast.error('Le titre est obligatoire.'); return; }
    if (!form.step_id.trim()) { toast.error('L\'identifiant est obligatoire.'); return; }

    // Valider JSON blocks
    let blocks: unknown[];
    try {
      const parsed = JSON.parse(form.blocksRaw);
      if (!Array.isArray(parsed)) throw new Error('Doit être un tableau JSON.');
      blocks = parsed;
    } catch (e) {
      toast.error(`Blocs JSON invalides : ${(e as Error).message}`);
      return;
    }

    const toastId = toast.loading('Enregistrement...');
    setSaving(true);
    try {
      const { error } = await supabase
        .from('parcours_steps')
        .update({
          title: form.title.trim(),
          step_id: form.step_id.trim(),
          description: form.description.trim() || null,
          icon: form.icon.trim() || null,
          status: finalStatus,
          blocks,
        })
        .eq('id', stepId!);

      if (error) throw error;

      setForm((prev) => ({ ...prev, status: finalStatus }));
      toast.success('Étape mise à jour.', { id: toastId });
    } catch (err: unknown) {
      const pg = err as { code?: string; message?: string };
      if (pg?.code === '23505') {
        toast.error('Cet identifiant d\'étape existe déjà pour ce parcours.', { id: toastId });
      } else {
        toast.error(pg?.message ?? 'Erreur lors de la sauvegarde.', { id: toastId });
      }
    } finally {
      setSaving(false);
    }
  }

  // ── États ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-600 border-t-transparent" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <p className="text-5xl font-bold text-slate-200 mb-4">404</p>
          <h1 className="text-2xl font-semibold text-slate-800 mb-2">Étape introuvable</h1>
          <p className="text-slate-500 mb-8">Cette étape n'existe pas ou n'appartient pas à ce parcours.</p>
          <Link
            to={`/admin/parcours/${id}/steps`}
            className="inline-flex items-center gap-2 text-sm font-medium text-pink-600 hover:text-pink-700"
          >
            <ChevronLeft className="h-4 w-4" /> Retour aux étapes
          </Link>
        </div>
      </div>
    );
  }

  const canViewPublic =
    parcours?.status === 'published' &&
    form.status === 'published' &&
    parcours?.slug &&
    form.step_id;

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>

      <div className="mx-auto max-w-5xl px-6 py-8">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Link
              to={`/admin/parcours/${id}/steps`}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Modifier l'étape</h1>
              {form.step_id && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {parcours?.company_name} · <code className="font-mono">{form.step_id}</code>
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {canViewPublic && (
              <a
                href={`/decouvrir/${parcours!.slug}/etape/${form.step_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Eye className="h-4 w-4" /> Voir
              </a>
            )}
            <button
              onClick={() => handleSave('draft')}
              disabled={saving || !!jsonError}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> Brouillon
            </button>
            <button
              onClick={() => handleSave('published')}
              disabled={saving || !!jsonError}
              className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Publier'}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_280px]">

          {/* Colonne principale */}
          <div className="space-y-6">

            {/* Informations */}
            <Section title="Informations">
              <Field label="Titre *">
                <input
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="ex : Notre culture d'entreprise"
                  className={inputClass}
                />
              </Field>
              <Field label="Identifiant *" hint="Utilisé dans l'URL">
                <input
                  value={form.step_id}
                  onChange={(e) => { set('step_id', e.target.value); setStepIdManuallyEdited(true); }}
                  placeholder="culture-entreprise"
                  className={inputClass}
                />
                {parcours?.slug && form.step_id && (
                  <p className="mt-1 text-xs text-slate-400">
                    URL : recruteurs.io/decouvrir/<strong>{parcours.slug}</strong>/etape/<strong>{form.step_id}</strong>
                  </p>
                )}
              </Field>
              <Field label="Description">
                <textarea
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  rows={3}
                  placeholder="Courte description de cette étape..."
                  className={`${inputClass} resize-y`}
                />
              </Field>
              <Field label="Icône" hint="Nom d'icône Lucide (optionnel, ex: building-2)">
                <input
                  value={form.icon}
                  onChange={(e) => set('icon', e.target.value)}
                  placeholder="building-2"
                  className={inputClass}
                />
              </Field>
            </Section>

            {/* Blocs de contenu */}
            <Section title="Blocs de contenu">
              {/* Toggle Visuel / JSON */}
              <div className="flex items-center justify-between mb-3">
                <div className="inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      // Vérifie que le JSON est valide avant de basculer en visuel
                      try {
                        JSON.parse(form.blocksRaw);
                        setEditorMode('visual');
                        setJsonError(null);
                      } catch (e) {
                        toast.error(
                          `Le JSON contient une erreur — corrigez-la avant de basculer en mode visuel (${(e as Error).message.slice(0, 80)})`,
                        );
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                      editorMode === 'visual'
                        ? 'bg-white text-[#30475e] shadow-sm'
                        : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    Visuel
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditorMode('json')}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                      editorMode === 'json'
                        ? 'bg-white text-[#30475e] shadow-sm'
                        : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    <Code2 className="w-3.5 h-3.5" />
                    JSON (avancé)
                  </button>
                </div>

                <p className="text-xs text-slate-500">
                  {!jsonError && (
                    <span className="text-emerald-600 font-medium">
                      ✓ {blocksCount(form.blocksRaw)} bloc{blocksCount(form.blocksRaw) !== 1 ? 's' : ''}
                    </span>
                  )}
                  {jsonError && (
                    <span className="text-red-600 font-medium">⚠ JSON invalide</span>
                  )}
                </p>
              </div>

              {/* MODE VISUEL — éditeur par formulaires */}
              {editorMode === 'visual' && !jsonError && (
                <BlocksEditor
                  blocks={(() => {
                    try {
                      const p = JSON.parse(form.blocksRaw);
                      return Array.isArray(p) ? p : [];
                    } catch {
                      return [];
                    }
                  })()}
                  onChange={(newBlocks) => {
                    const json = JSON.stringify(newBlocks, null, 2);
                    set('blocksRaw', json);
                    setJsonError(null);
                  }}
                />
              )}

              {/* MODE JSON — textarea brut (avancé) */}
              {editorMode === 'json' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500">
                      Tableau JSON des blocs (édition avancée).
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          const parsed = JSON.parse(form.blocksRaw);
                          set('blocksRaw', JSON.stringify(parsed, null, 2));
                          setJsonError(null);
                        } catch {
                          // keep error
                        }
                      }}
                      className="text-xs text-slate-400 hover:text-slate-700 transition-colors"
                    >
                      Formater
                    </button>
                  </div>
                  <textarea
                    value={form.blocksRaw}
                    onChange={(e) => handleBlocksChange(e.target.value)}
                    rows={16}
                    spellCheck={false}
                    className={`w-full rounded-xl border bg-white px-3 py-2.5 font-mono text-xs text-slate-900 placeholder-slate-400 outline-none transition resize-y focus:ring-1 ${
                      jsonError
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-400'
                        : 'border-slate-300 focus:border-slate-900 focus:ring-slate-900'
                    }`}
                    placeholder={'[\n  {\n    "type": "rich-text",\n    "html": "<p>Votre contenu</p>"\n  }\n]'}
                  />
                  {jsonError && (
                    <p className="text-xs text-red-500">JSON invalide : {jsonError}</p>
                  )}

                  {/* Alerte si types de blocs inconnus */}
                  {!jsonError && unknownBlockTypes(form.blocksRaw).length > 0 && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      <strong>⚠️ Type{unknownBlockTypes(form.blocksRaw).length > 1 ? 's' : ''} inconnu{unknownBlockTypes(form.blocksRaw).length > 1 ? 's' : ''} :</strong>{' '}
                      {unknownBlockTypes(form.blocksRaw).map((t) => (
                        <code key={t} className="rounded bg-white px-1 py-0.5 mx-0.5 ring-1 ring-amber-200 font-mono">{t}</code>
                      ))}
                      {' '}— ce{unknownBlockTypes(form.blocksRaw).length > 1 ? 's blocs' : ' bloc'} ne sera pas rendu sur le site.
                      Types acceptés : <code>rich-text</code>, <code>image</code>, <code>video</code>, <code>embed-iframe</code>, <code>pdf</code>, <code>stats-grid</code> (alias <code>stats</code>), <code>table</code>, <code>timeline</code>, <code>team-org</code>.
                    </div>
                  )}
                </div>
              )}
            </Section>

          </div>

          {/* Colonne droite */}
          <div className="space-y-5">
            <div className="sticky top-6 space-y-5">

              {/* Publication */}
              <Section title="Publication">
                <Field label="Statut">
                  <select
                    value={form.status}
                    onChange={(e) => set('status', e.target.value as StepStatus)}
                    className={inputClass}
                  >
                    <option value="draft">Brouillon</option>
                    <option value="published">Publié</option>
                  </select>
                </Field>
              </Section>

              {/* Informations */}
              <Section title="Informations">
                <div className="space-y-2.5">
                  {meta && (
                    <InfoRow label="Ordre" value={String(meta.order_index)} />
                  )}
                  <InfoRow label="Blocs" value={`${blocksCount(form.blocksRaw)}${jsonError ? ' (JSON invalide)' : ''}`} />
                  {parcours && (
                    <InfoRow label="Parcours" value={parcours.company_name} />
                  )}
                  {canViewPublic && (
                    <a
                      href={`/decouvrir/${parcours!.slug}/etape/${form.step_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 flex items-center gap-1.5 text-sm text-pink-600 hover:text-pink-700 transition-colors"
                    >
                      <Eye className="h-3.5 w-3.5" /> Voir la page publique
                    </a>
                  )}
                </div>
              </Section>

              {/* Navigation */}
              <Section title="Navigation">
                <div className="space-y-2">
                  <Link
                    to={`/admin/parcours/${id}/steps`}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4 text-slate-400" /> Toutes les étapes
                  </Link>
                  <Link
                    to={`/admin/parcours/${id}/edit`}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4 text-slate-400" /> Modifier le parcours
                  </Link>
                </div>
              </Section>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Sous-composants ────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="mb-5 text-base font-bold text-slate-900">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {hint && <span className="ml-1.5 text-xs font-normal text-slate-400">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <span className="text-xs text-slate-700 text-right">{value}</span>
    </div>
  );
}

const inputClass =
  'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-slate-900 focus:ring-1 focus:ring-slate-900';

// ── Templates de blocs ────────────────────────────────────────────────────────

const BLOCK_TEMPLATES: Record<string, unknown> = {
  'rich-text': {
    type: 'rich-text',
    html: '<p>Votre contenu ici.</p>',
  },
  'image': {
    type: 'image',
    url: '',
    alt: '',
    caption: '',
    align: 'center',
  },
  'video': {
    type: 'video',
    url: '',
    title: '',
  },
  'stats-grid': {
    type: 'stats-grid',
    items: [
      { value: '', label: '' },
      { value: '', label: '' },
    ],
  },
  'table': {
    type: 'table',
    columns: ['Colonne 1', 'Colonne 2'],
    rows: [['Valeur', 'Valeur']],
  },
  'timeline': {
    type: 'timeline',
    items: [
      { year: '', headline: '', description: '' },
    ],
  },
  'team-org': {
    type: 'team-org',
    title: 'Vos interlocuteurs au sein de l\'entreprise',
    description: 'Découvrez les personnes clés de l\'entreprise.',
    levels: [
      {
        title: 'Direction',
        members: [
          {
            name: 'Prénom Nom',
            role: 'Directeur général',
            photoUrl: '',
            linkedinUrl: '',
            highlight: true,
          },
        ],
      },
      {
        title: 'Management',
        members: [
          {
            name: 'Prénom Nom',
            role: 'Directeur adjoint',
            photoUrl: '',
            linkedinUrl: '',
          },
        ],
      },
      {
        title: 'Équipe',
        members: [
          {
            name: 'Prénom Nom',
            role: 'Fonction',
            photoUrl: '',
            linkedinUrl: '',
          },
          {
            name: 'Prénom Nom',
            role: 'Fonction',
            photoUrl: '',
            linkedinUrl: '',
          },
        ],
      },
    ],
  },
  'embed-iframe': {
    type: 'embed-iframe',
    src: '',
    title: 'Formulaire',
    height: 600,
  },
  'pdf': {
    type: 'pdf',
    url: '',
    title: 'Document',
  },
};
